import React from 'react';
import type { RouteRecord } from 'vite-react-ssg';
import Layout from './Layout';
import Index from './pages/Index';
import RouteError from './components/RouteError';
import manifest from './i18n/route-manifest.json';
import NotFound from './pages/NotFound';
import { LocaleProvider } from './i18n/LocaleProvider';
import { translationAvailable } from './i18n/availability';
import { TranslationBoundary } from './i18n/TranslationBoundary';
import { SITE_LOCALE } from './i18n/locale';
import { blogPostSlugs } from './data/blogSlugs';
import { caseStudySlugs } from './data/caseStudies';

// Code-split every non-home route. vite-react-ssg resolves these `lazy` functions at
// prerender time (so each route's content still lands in static HTML for SEO/AEO) and
// ships them as separate chunks at runtime, so a visitor only downloads the JS for the
// page they're on. Our pages use `export default`, so map it to React Router's
// `Component`. The `import(...)` must be DIRECTLY inside the lazy fn so vite-react-ssg
// can detect each route's style/asset deps during the build.
// The homepage (Index) stays eager — it's the most-hit route and the LCP target, so we
// don't want it behind an extra chunk request.

const englishRoutes: RouteRecord[] = [
  {
    path: '/',
    element: <Layout />,
    // Catch any error thrown while rendering a route: report it to Sentry and show a
    // friendly screen instead of React Router's default "Unexpected Application Error!".
    errorElement: <LocaleProvider><RouteError /></LocaleProvider>,
    children: [
      { index: true, element: <Index /> },
      { path: 'cloud', lazy: async () => ({ Component: (await import('./pages/GizmoDataCloudPage')).default }) },
      { path: 'cloud/at-customer', lazy: async () => ({ Component: (await import('./pages/CloudAtCustomerPage')).default }) },
      { path: 'cloud/snowflake-alternative', lazy: async () => ({ Component: (await import('./pages/CloudSnowflakeAlternativePage')).default }) },
      { path: 'cloud/databricks-alternative', lazy: async () => ({ Component: (await import('./pages/CloudDatabricksAlternativePage')).default }) },
      { path: 'hardcharger', lazy: async () => ({ Component: (await import('./pages/HardchargerPage')).default }) },
      { path: 'hardcharger/install', lazy: async () => ({ Component: (await import('./pages/HardchargerInstallPage')).default }) },
      { path: 'hardcharger/integrations', lazy: async () => ({ Component: (await import('./pages/HardchargerIntegrationsPage')).default }) },
      { path: 'chargewire', lazy: async () => ({ Component: (await import('./pages/ChargewirePage')).default }) },
      // GizmoSQL 1.x (legacy, in maintenance; Hardcharger is its successor). URLs kept as they are.
      { path: 'gizmosql', lazy: async () => ({ Component: (await import('./pages/GizmoSQLPage')).default }) },
      { path: 'gizmosql/enterprise', lazy: async () => ({ Component: (await import('./pages/EnterpriseFeaturesPage')).default }) },
      { path: 'gizmosql/install', lazy: async () => ({ Component: (await import('./pages/InstallPage')).default }) },
      { path: 'gizmosql/ui', lazy: async () => ({ Component: (await import('./pages/GizmoSQLUIPage')).default }) },
      { path: 'gizmosql/mcp', lazy: async () => ({ Component: (await import('./pages/GizmoSQLMcpPage')).default }) },
      { path: 'gizmosql/integrations', lazy: async () => ({ Component: (await import('./pages/GizmoSQLIntegrationsPage')).default }) },
      // Hardcharger comparison and use-case pages (message-matched to Google Ads campaigns).
      { path: 'hardcharger/vs-snowflake', lazy: async () => ({ Component: (await import('./pages/VsSnowflakePage')).default }) },
      { path: 'hardcharger/vs-databricks', lazy: async () => ({ Component: (await import('./pages/VsDatabricksPage')).default }) },
      { path: 'hardcharger/vs-bigquery', lazy: async () => ({ Component: (await import('./pages/VsBigQueryPage')).default }) },
      { path: 'hardcharger/ai-agents', lazy: async () => ({ Component: (await import('./pages/AiAgentsPage')).default }) },
      { path: 'hardcharger/offload', lazy: async () => ({ Component: (await import('./pages/OffloadWorkloadsPage')).default }) },
      { path: 'cut-your-warehouse-bill', lazy: async () => ({ Component: (await import('./pages/CutYourWarehouseBillPage')).default }) },
      { path: 'cut-your-bigquery-bill', lazy: async () => ({ Component: (await import('./pages/CutYourBigQueryBillPage')).default }) },
      { path: 'duckdb-sql-server', lazy: async () => ({ Component: (await import('./pages/DuckDbServerPage')).default }) },
      { path: 'arrow-flight-sql', lazy: async () => ({ Component: (await import('./pages/ArrowFlightSqlPage')).default }) },
      // ADBC driver suite — hub + one page per driver.
      { path: 'adbc-drivers', lazy: async () => ({ Component: (await import('./pages/AdbcDriversPage')).default }) },
      { path: 'adbc-drivers/hardcharger', lazy: async () => ({ Component: (await import('./pages/AdbcDriverHardchargerPage')).default }) },
      { path: 'adbc-drivers/gizmosql', lazy: async () => ({ Component: (await import('./pages/AdbcDriverGizmoSQLPage')).default }) },
      { path: 'adbc-drivers/oracle', lazy: async () => ({ Component: (await import('./pages/AdbcDriverOraclePage')).default }) },
      { path: 'adbc-drivers/db2', lazy: async () => ({ Component: (await import('./pages/AdbcDriverDb2Page')).default }) },
      { path: 'adbc-drivers/quack', lazy: async () => ({ Component: (await import('./pages/AdbcDriverQuackPage')).default }) },
      { path: 'gizmoedge', lazy: async () => ({ Component: (await import('./pages/GizmoEdgePage')).default }) },
      { path: 'gizmoedge-privacy-policy-1', lazy: async () => ({ Component: (await import('./pages/GizmoEdgePrivacyPolicy')).default }) },
      { path: 'about', lazy: async () => ({ Component: (await import('./pages/AboutPage')).default }) },
      { path: 'blog', lazy: async () => ({ Component: (await import('./pages/BlogPage')).default }) },
      // Prerender each static blog post to its own HTML for SEO/AEO.
      // getStaticPaths needs the FULL resolved path (nested route), not the bare slug.
      {
        path: 'blog/:slug',
        lazy: async () => ({ Component: (await import('./pages/BlogPostPage')).default }),
        getStaticPaths: () => blogPostSlugs.map(slug => `/blog/${slug}`),
      },
      { path: 'customers', lazy: async () => ({ Component: (await import('./pages/CustomersPage')).default }) },
      { path: 'case-studies', lazy: async () => ({ Component: (await import('./pages/CaseStudiesPage')).default }) },
      {
        path: 'case-studies/aalborg-university-dess',
        lazy: async () => ({ Component: (await import('./pages/DessCaseStudyPage')).default }),
      },
      {
        path: 'case-studies/:slug',
        lazy: async () => ({ Component: (await import('./pages/CaseStudyPage')).default }),
        getStaticPaths: () => caseStudySlugs.map(slug => `/case-studies/${slug}`),
      },
      { path: 'partners', lazy: async () => ({ Component: (await import('./pages/PartnersPage')).default }) },
      { path: 'privacy-policy', lazy: async () => ({ Component: (await import('./pages/PrivacyPolicy')).default }) },
      { path: 'terms-of-service', lazy: async () => ({ Component: (await import('./pages/TermsOfService')).default }) },
      { path: 'refund-policy', lazy: async () => ({ Component: (await import('./pages/RefundPolicy')).default }) },
      { path: 'careers', lazy: async () => ({ Component: (await import('./pages/CareersPage')).default }) },
      { path: 'services', lazy: async () => ({ Component: (await import('./pages/ServicesPage')).default }) },
      { path: 'news', lazy: async () => ({ Component: (await import('./pages/NewsPage')).default }) },
      { path: 'sms-alerts', lazy: async () => ({ Component: (await import('./pages/SmsAlertsPage')).default }) },
      { path: 'contact', lazy: async () => ({ Component: (await import('./pages/ContactPage')).default }) },
      { path: 'contact/thank-you', lazy: async () => ({ Component: (await import('./pages/ContactThankYouPage')).default }) },
      { path: 'thank-you', lazy: async () => ({ Component: (await import('./pages/ThankYouPage')).default }) },
      { path: '*', lazy: async () => ({ Component: (await import('./components/RouteFallback')).default }) },
    ],
  },
];

// One language per build: gizmodata.com gets the English routes, gizmodata.com.br the Portuguese ones at the
// same paths (src/i18n/locale.ts). Both reuse the same components and lazy imports. Portuguese includes only
// the routes with a complete translation; TranslationBoundary answers the rest (and untranslated articles
// reached by client-side navigation) with the 404 page, which links to the English page.
const children = englishRoutes[0].children!;
const portugueseChildren: RouteRecord[] = children.filter(route => route.path === '*' || route.getStaticPaths || translationAvailable(route.index ? '/' : '/' + route.path)).map(route => ({
  ...route,
  ...(route.getStaticPaths ? {
    getStaticPaths: () => manifest
      .filter(r => (route.path === 'blog/:slug' ? r.path.startsWith('/blog/') : route.path === 'case-studies/:slug' ? r.path.startsWith('/case-studies/') : false) && r.ptBRComplete)
      .map(r => r.path),
  } : {}),
}));
const siteChildren: RouteRecord[] = SITE_LOCALE === 'pt-BR'
  ? [{ element: <TranslationBoundary />, children: portugueseChildren.filter(r => r.path !== '*') }, { path: '404', element: <NotFound /> }, portugueseChildren.find(r => r.path === '*')!]
  : [...children.filter(r => r.path !== '*'), { path: '404', element: <NotFound /> }, children.find(r => r.path === '*')!];
export const routes: RouteRecord[] = [{
  ...englishRoutes[0],
  index: false,
  children: siteChildren,
}];
